import { getRandomInt, shuffle } from "../Shared/Helpers.tsx";
import { MathRun, mathPlain } from "../Shared/Pdf/mathRuns.ts";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { greatestCommonDivisor } from "./FractionGenerator.ts";
import { generateExponentRulePages, randomExponentRuleProblem } from "./ExponentRuleGenerator.ts";
import { NumberPropertyOptions, ProblemData, SUPERSCRIPT, isRootQuestion } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import * as enNP from "../Files/Prompts/en/numberProperty.ts";
import * as esNP from "../Files/Prompts/es/numberProperty.ts";
import * as frNP from "../Files/Prompts/fr/numberProperty.ts";

const NUMBER_PROPERTY: Record<PrintedLanguage, typeof enNP> = { en: enNP, es: esNP, fr: frNP };

/**
 * Number property generation - factors, multiples, GCF, LCM, even and odd.
 * See ADR 0032.
 *
 * `greatestCommonDivisor` is reused from the fraction generator rather than
 * rewritten. ADR 0022 §2 named that export as the reason this strand is cheap,
 * and it is.
 *
 * The constraint that shapes the file is ADR 0032 §3: a Factors answer is a
 * list, and 0022 §1 test 3 allows only a *short* one. Numbers with more than
 * eight factors are not drawn at all, so the key never prints a paragraph.
 */

/** ADR 0032 §3. 96 has twelve factors; 24 has eight and is the practical ceiling. */
export const MAX_FACTORS = 8;

/**
 * And a floor, which the ADR did not anticipate.
 *
 * Rejecting numbers with too many factors biases the draw hard toward numbers
 * with very few: a generated sheet at a ceiling of 144 came back with four
 * primes out of sixteen, whose whole answer is "1, 29". Four factors excludes
 * primes and leaves the semi-primes, which are thin but genuine.
 */
export const MIN_FACTORS = 4;

/**
 * Floors on the numbers a question is asked about.
 *
 * Both are here because a first draft asked "Is 7 even or odd?" on a sheet
 * that also asked for the LCM of 24 and 42. Below ten, even and odd is a
 * counting question rather than a look-at-the-last-digit one, and the factors
 * of 4 are not practice.
 */
const FACTOR_FLOOR = 6;
const EVEN_ODD_FLOOR = 10;

/**
 * `Is 4 prime or composite?` is answered by counting rather than by looking at
 * a factor list, which is the skill. And 1 is never drawn at all: it is
 * neither prime nor composite, and a worksheet is the wrong place to litigate
 * that (ADR 0037 §3).
 */
const PRIME_FLOOR = 10;

/**
 * Bases a square or cube is taken of.
 *
 * Capped at 12 for cubes because 12³ is 1,728 and 20³ is 8,000 - a different
 * kind of question. From 2, because a power of 1 asks nothing.
 */
const LARGEST_SQUARE_BASE = 20;
const LARGEST_CUBE_BASE = 12;

/** Every whole number that divides `value` exactly, in order. */
export function factorsOf(value: number): number[] {
    const factors: number[] = [];
    for (let candidate = 1; candidate <= value; candidate++) {
        if (value % candidate === 0) {
            factors.push(candidate);
        }
    }
    return factors;
}

/** a * b / gcd(a, b) - computed, not searched for. */
export function leastCommonMultiple(a: number, b: number): number {
    return (a * b) / greatestCommonDivisor(a, b);
}

/**
 * Which question types a setting asks for. Mixed means all seven;
 * FactorsAndMultiples is the narrower curriculum-unit mix from ADR 0041 §3 -
 * the same kind of split Time already makes between its clock-reading and
 * elapsed-time mixes.
 */
function questionTypes(question: NumberPropertyOptions): NumberPropertyOptions[] {
    if (question === NumberPropertyOptions.Mixed) {
        return [
            NumberPropertyOptions.Factors,
            NumberPropertyOptions.Multiples,
            NumberPropertyOptions.GCF,
            NumberPropertyOptions.LCM,
            NumberPropertyOptions.EvenOdd,
            NumberPropertyOptions.Powers,
            NumberPropertyOptions.PrimeComposite,
        ];
    }
    if (question === NumberPropertyOptions.FactorsAndMultiples) {
        return [
            NumberPropertyOptions.Factors,
            NumberPropertyOptions.Multiples,
            NumberPropertyOptions.GCF,
            NumberPropertyOptions.LCM,
        ];
    }
    return [question];
}

function usableMax(maxValue: number): number {
    return Math.max(12, Math.floor(maxValue) || 12);
}

/**
 * A number whose factor list is short enough to print.
 *
 * Drawn by rejection rather than by construction, which is the exception in
 * this repo and is deliberate here: "has at most eight factors" is a property
 * of the number, not something an answer-first draw could arrange. The search
 * is bounded, and 2 is the fallback because every range contains it.
 */
function numberWithShortFactorList(max: number): number {
    for (let attempt = 0; attempt < 60; attempt++) {
        const candidate = getRandomInt(FACTOR_FLOOR, max);
        const count = factorsOf(candidate).length;
        if (count >= MIN_FACTORS && count <= MAX_FACTORS) {
            return candidate;
        }
    }
    // Six has exactly four factors, so the fallback satisfies both bounds.
    return 6;
}

/**
 * Two numbers with a common factor worth finding.
 *
 * Built from a shared factor and two coprime multipliers, so the GCF is that
 * shared factor and the LCM is bounded by construction. Drawing two numbers at
 * random instead gives a GCF of 1 most of the time - which is a correct answer
 * to a question not worth asking - and an LCM in the thousands whenever both
 * happen to be prime.
 */
function pairWithCommonFactor(max: number): [number, number] {
    const ceiling = Math.max(2, Math.floor(max / 4));
    for (let attempt = 0; attempt < 60; attempt++) {
        const shared = getRandomInt(2, Math.min(12, ceiling));
        const first = getRandomInt(2, 8);
        const second = getRandomInt(2, 8);
        if (first === second || greatestCommonDivisor(first, second) !== 1) {
            continue;
        }
        if (shared * first <= max && shared * second <= max) {
            return [shared * first, shared * second];
        }
    }
    return [12, 18];
}

function buildProblem(
    question: NumberPropertyOptions,
    max: number,
    language: PrintedLanguage
): { prompt: string; answer: string } {
    const { numberPropertyPrompts: t, primeCompositeLabels, evenOddLabels } = NUMBER_PROPERTY[language];
    switch (question) {
        case NumberPropertyOptions.Multiples: {
            const count = getRandomInt(4, 5);
            const base = getRandomInt(2, 12);
            const multiples = Array.from({ length: count }, (_, i) => base * (i + 1));
            return {
                prompt: t.multiples(count, base),
                answer: multiples.join(", "),
            };
        }
        case NumberPropertyOptions.GCF: {
            const [first, second] = pairWithCommonFactor(max);
            return {
                prompt: t.gcf(first, second),
                answer: String(greatestCommonDivisor(first, second)),
            };
        }
        case NumberPropertyOptions.LCM: {
            const [first, second] = pairWithCommonFactor(max);
            return {
                prompt: t.lcm(first, second),
                answer: String(leastCommonMultiple(first, second)),
            };
        }
        case NumberPropertyOptions.Powers: {
            // Two or three only, and ADR 0037 §2 records why: ⁴ is not in
            // WinAnsi and ADR 0014 §3 refuses to embed a font. Pure notation -
            // no words, so no translation needed here.
            const exponent: 2 | 3 = getRandomInt(0, 1) === 0 ? 2 : 3;
            const largest = exponent === 2 ? LARGEST_SQUARE_BASE : LARGEST_CUBE_BASE;
            const base = getRandomInt(2, Math.max(2, Math.min(largest, max)));
            return {
                prompt: `${base}${SUPERSCRIPT[exponent]} = ?`,
                answer: String(base ** exponent),
            };
        }
        case NumberPropertyOptions.PrimeComposite: {
            const value = getRandomInt(Math.min(PRIME_FLOOR, max), max);
            return {
                prompt: t.primeComposite(value),
                answer: factorsOf(value).length === 2 ? primeCompositeLabels.prime : primeCompositeLabels.composite,
            };
        }
        case NumberPropertyOptions.EvenOdd: {
            const value = getRandomInt(Math.min(EVEN_ODD_FLOOR, max), max);
            return {
                prompt: t.evenOdd(value),
                answer: value % 2 === 0 ? evenOddLabels.even : evenOddLabels.odd,
            };
        }
        default: {
            const value = numberWithShortFactorList(max);
            return {
                prompt: t.factors(value),
                answer: factorsOf(value).join(", "),
            };
        }
    }
}

/**
 * The roots the three root types (ADR 0132 §3) are asked about, lowest and
 * highest: square roots of 4 to 225, cube roots of 8 to 1000, and an estimated
 * root whose lower whole number runs from 1 (so 1 and 2) to 14 (14 and 15).
 * The largest-number dial does not apply to them.
 */
const ROOT_RANGE: Partial<Record<NumberPropertyOptions, [number, number]>> = {
    [NumberPropertyOptions.SquareRoots]: [2, 15],
    [NumberPropertyOptions.CubeRoots]: [2, 10],
    [NumberPropertyOptions.EstimatingRoots]: [1, 14],
};

function rootsFor(question: NumberPropertyOptions): number[] {
    const [low, high] = ROOT_RANGE[question]!;
    return Array.from({ length: high - low + 1 }, (_, i) => low + i);
}

/**
 * One page's roots, dealt as a shuffled deck (ADR 0132 §3): no root repeats on
 * a page until every root in the range has been used, and when a page is longer
 * than the range a second deck follows that does not open with the root the
 * first closed on, so neighbours are never equal.
 */
export function dealRoots(roots: number[], count: number): number[] {
    const dealt: number[] = [];
    while (dealt.length < count) {
        const deck = shuffle(roots);
        if (dealt.length > 0 && deck.length > 1 && deck[0] === dealt[dealt.length - 1]) {
            [deck[0], deck[1]] = [deck[1], deck[0]];
        }
        dealt.push(...deck);
    }
    return dealt.slice(0, count);
}

/**
 * A root problem from its root (ADR 0132). The prompt travels as `mathPrompt`
 * for `MathText` to draw, and `display[0]` is the same prompt as one Unicode
 * string. An estimating question picks its radicand between the root's square
 * and the next, so a small radicand is as likely as a large one.
 */
export function buildRootProblem(
    question: NumberPropertyOptions,
    root: number,
    key: number,
    language: PrintedLanguage = 'en'
): ProblemData {
    let prompt: MathRun[];
    let answer: string;
    if (question === NumberPropertyOptions.CubeRoots) {
        prompt = [{ root: String(root ** 3), index: 3 }, { text: ' = ?' }];
        answer = String(root);
    } else if (question === NumberPropertyOptions.SquareRoots) {
        prompt = [{ root: String(root ** 2) }, { text: ' = ?' }];
        answer = String(root);
    } else {
        const { numberPropertyPrompts: t, rootPairKey } = NUMBER_PROPERTY[language];
        const radicand = getRandomInt(root ** 2 + 1, (root + 1) ** 2 - 1);
        prompt = t.estimatingRoots(radicand);
        answer = rootPairKey(root, root + 1);
    }
    return {
        key,
        numbers: [],
        operator: "",
        display: [mathPlain(prompt), answer],
        numberPropertyQuestion: question,
        mathPrompt: prompt,
    };
}

/**
 * One number-property problem. Always succeeds.
 *
 * `display` is `[prompt, answer]`; `numberPropertyQuestion` records which kind
 * it is. Per-format data goes in its own field and never `numbers[3]`.
 */
export function buildNumberPropertyProblem(
    question: NumberPropertyOptions,
    maxValue: number,
    key: number,
    language: PrintedLanguage = 'en'
): ProblemData {
    if (isRootQuestion(question)) {
        const [low, high] = ROOT_RANGE[question]!;
        return buildRootProblem(question, getRandomInt(low, high), key, language);
    }
    if (question === NumberPropertyOptions.ExponentRules) {
        return randomExponentRuleProblem(key, language);
    }
    const built = buildProblem(question, usableMax(maxValue), language);
    return {
        key,
        numbers: [],
        operator: "",
        display: [built.prompt, built.answer],
        numberPropertyQuestion: question,
    };
}

/** A page-shaped set of number-property problems. */
export function generateNumberPropertyPages(settings: ISettingsProps): ProblemData[][] | null {
    const question = settings.NumberPropertyQuestion ?? NumberPropertyOptions.Mixed;
    if (isRootQuestion(question)) {
        return generateRootPages(settings, question);
    }
    if (question === NumberPropertyOptions.ExponentRules) {
        return generateExponentRulePages(settings);
    }
    const types = questionTypes(settings.NumberPropertyQuestion ?? NumberPropertyOptions.Mixed);
    const maxValue = settings.NumberPropertyMaxValue ?? 50;
    const language = printedLanguageOf(settings);
    return generateProblemPages({
        settings,
        choices: types,
        build: (question, key) => buildNumberPropertyProblem(question, maxValue, key, language),
        fingerprint: (problem) => problem.display![0],
    });
}

/**
 * The root types deal a shuffled deck of roots on every page (ADR 0132 §3), so
 * they pass `dealPage`; the other types keep dealing over the whole run, and
 * their output is byte-identical (`NumberPropertyGolden.test.ts`).
 *
 * The de-duplication fingerprint is the prompt within its page. Past the end of
 * the deck a repeat is required, so a position there is never a duplicate: it
 * would only burn the attempt budget on a problem that cannot change.
 */
function generateRootPages(settings: ISettingsProps, question: NumberPropertyOptions): ProblemData[][] | null {
    const roots = rootsFor(question);
    const language = printedLanguageOf(settings);
    const perPage = settings.NumberOfProblemsPerPage;
    return generateProblemPages({
        settings,
        choices: roots,
        build: (root, key) => buildRootProblem(question, root, key, language),
        fingerprint: (problem) => {
            const page = Math.floor((problem.key - 1) / perPage);
            const position = (problem.key - 1) % perPage;
            return position < roots.length ? `${page}:${problem.display![0]}` : `repeat:${problem.key}`;
        },
        dealPage: (count) => dealRoots(roots, count),
    });
}
